/* @HEADER
 * @version 1.1.851 | 2026-10-24
 * @purpose ZVX-DEV-8226 — which project a window moves to when the project it stands on was deleted on another device: the same organisation's neighbour, never another organisation.
 * @sidecar chooseProjectAfterRemoteDelete.ts.header.md
 * @layer lib | @hex _none_ | @ctx project
 * @public true
 * @edit careful
 */

// SpecRefs: ZVX-DEV-8226

import { resolveProjectOrgId, type OrgScopeProject } from './projectOrgScope.ts'

export interface ChooseAfterRemoteDeleteInput {
  /** The project that was deleted. */
  removedId: string
  /** The organisation it belonged to; with none known there is no safe answer. */
  organisationId: string | undefined
  /** The organisation's project ids in switcher order, as they stood when the project was removed (the removed one included). */
  orderedIds: readonly string[]
  /** The projects that are live and visible to this person now, in registry order. */
  projects: readonly (OrgScopeProject & { id: string })[]
  /** Every project removed in the same pull: a neighbour that is going too is not a neighbour. */
  alsoRemovedIds?: readonly string[]
  personalWorkspaceId?: string | undefined
}

/**
 * The first still-live project that stood AFTER the removed one in the switcher order; if there is none, the last one that
 * stood BEFORE it; if the removed one had no known place, the organisation's first live project; `null` when the
 * organisation has none left. A project of another organisation is never the answer, and neither is the removed one.
 *
 * The neighbour, not «the first of the organisation»: the person can predict it from the list she already sees.
 */
export function chooseProjectAfterRemoteDelete(input: ChooseAfterRemoteDeleteInput): string | null {
  const { removedId, organisationId, orderedIds, projects, alsoRemovedIds, personalWorkspaceId } = input
  if (!organisationId) return null
  const gone = new Set([removedId, ...(alsoRemovedIds ?? [])])
  const members = projects.filter(
    (p) => !gone.has(p.id) && resolveProjectOrgId(p, personalWorkspaceId) === organisationId,
  )
  if (members.length === 0) return null
  const live = new Set(members.map((p) => p.id))
  const place = orderedIds.indexOf(removedId)
  if (place >= 0) {
    const after = orderedIds.slice(place + 1).find((id) => live.has(id))
    if (after !== undefined) return after
    const before = orderedIds.slice(0, place).reverse().find((id) => live.has(id))
    if (before !== undefined) return before
  }
  return members[0].id
}
